import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MdAdd, MdCategory, MdConfirmationNumber, MdExpandLess, MdExpandMore } from "react-icons/md";
import { toast } from "react-toastify";
import Card from "../../../components/card";
import SearchableSelect from "../../../components/searchableSelect/SearchableSelect";
import SkeletonTable from "../../../components/skeleton/table";
import PageHeader, { FilterField } from "../../../components/page/PageHeader";
import KpiStrip, { KpiItem } from "../../../components/page/KpiStrip";
import { fmtPercent, labelMes, slaTone, TONE_TEXT, ultimosMeses } from "../../../components/slaSummary/slaFormat";
import api from "../../../services/axios";
import {
  getTickets,
  getTicketCategories,
  getTicketQueues,
  getTicketSlaCompliance,
} from "../../../services/ticketApi";
import { getEmailFromJwt } from "../../../utils/jwtUtils";
import { usePermissions } from "../../../auth/PermissionProvider";
import { Permissions } from "../../../auth/permissions";
import { Ticket, TicketCategory, TicketQueue, TicketSlaCompliance, TicketState } from "../../../interfaces/Ticket";
import SlaDetails from "./components/SlaDetails";
import TicketTable from "./components/TicketTable";
import TicketDetailModal from "./components/TicketDetailModal";
import NewTicketModal from "./components/NewTicketModal";
import CategoriesModal from "./components/CategoriesModal";

interface EmployeeOption {
  id: number;
  name: string;
  email?: string;
}

interface ClientOption {
  id: number;
  name: string;
}

/** Valores especiais do filtro de colaborador; o resto é o id do colaborador. */
const SEM_RESPONSAVEL = "none";

type Aba = "abertos" | "atrasados" | "pendente" | "resolvidos" | "fechados" | "todos";

const ENCERRADOS = [TicketState.Resolvido, TicketState.Fechado];
const emAberto = (t: Ticket) => !ENCERRADOS.includes(t.state);

/**
 * O estado é uma VISÃO da lista, não um filtro da tela: o indicador de SLA mede o desfecho, e
 * filtrar "só abertos" tiraria os resolvidos da conta. Por isso ele mora em abas da tabela,
 * filtradas no navegador sobre a mesma consulta, e cada aba mostra quantos tem.
 */
const ABAS: { key: Aba; label: string; filtro: (t: Ticket) => boolean }[] = [
  { key: "abertos", label: "Em aberto", filtro: emAberto },
  { key: "atrasados", label: "Atrasados", filtro: (t) => emAberto(t) && t.slaResolutionBreached },
  { key: "pendente", label: "Pendente do cliente", filtro: (t) => t.state === TicketState.PendenteCliente },
  { key: "resolvidos", label: "Resolvidos", filtro: (t) => t.state === TicketState.Resolvido },
  { key: "fechados", label: "Fechados", filtro: (t) => t.state === TicketState.Fechado },
  { key: "todos", label: "Todos", filtro: () => true },
];

const DETALHES_KEY = "nina.chamados.slaDetalhes";

const lerDetalhes = () => {
  try {
    return localStorage.getItem(DETALHES_KEY) === "1";
  } catch {
    return false;
  }
};

const selectCls =
  "h-9 w-full rounded-lg border border-gray-200 bg-white px-2.5 text-sm text-navy-700 dark:border-white/15 dark:bg-navy-900 dark:text-white";

/**
 * Tela interna de atendimento de chamados.
 *
 * Uma barra de filtros só (mês, cliente, fila, colaborador) no cabeçalho, e TODO bloco abaixo
 * responde a ela: os indicadores, o detalhe do SLA e a lista. Os indicadores são clicáveis e
 * viram filtro ("Sem responsável", "Atrasados"), e a lista vem logo abaixo do cabeçalho — é ela
 * que quem atende usa o dia inteiro; o detalhe do SLA fica recolhido.
 *
 * Board/kanban ficou fora de propósito — a operação real (Tarefas) já tem um, e duplicar o board
 * aqui faria o chamado brigar com a tarefa por qual dos dois é a fonte da verdade sobre "andamento".
 */
const TicketsPage: React.FC = () => {
  const { can } = usePermissions();
  const canManage = can(Permissions.TicketsManage);
  const canAdmin = can(Permissions.TicketsAdmin);

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [sla, setSla] = useState<TicketSlaCompliance | null>(null);
  const [queues, setQueues] = useState<TicketQueue[]>([]);
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // Filtros da TELA. "Todos os meses" por padrão: a lista é uma fila de trabalho, e abrir no mês
  // corrente esconderia o chamado de setembro que ainda está aberto.
  const [month, setMonth] = useState<string | null>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const [queueId, setQueueId] = useState<number | null>(null);
  const [assignee, setAssignee] = useState<string | null>(null);
  const [aba, setAba] = useState<Aba>("abertos");
  const [detalhes, setDetalhes] = useState(lerDetalhes);

  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  // Sem claim de employeeId no JWT: cruza o e-mail do token com a lista de colaboradores, mesma
  // técnica de `capacity/index.tsx`. Serve para "Assumir para mim" — o servidor é a autoridade.
  const myEmployeeId = useMemo(() => {
    const email = getEmailFromJwt(localStorage.getItem("jwtToken"))?.toLowerCase();
    if (!email) return null;
    return employees.find((e) => e.email?.toLowerCase() === email)?.id ?? null;
  }, [employees]);

  // Só as ATIVAS: é o catálogo que os formulários oferecem. A tela de administração pede as
  // desativadas por conta própria, porque só ela precisa delas (para reativar).
  const carregarCategorias = useCallback(() => {
    getTicketCategories()
      .then(setCategories)
      .catch((e) => console.error("Erro ao carregar categorias", e));
  }, []);

  useEffect(() => {
    carregarCategorias();

    getTicketQueues()
      .then(setQueues)
      .catch((e) => {
        console.error("Erro ao carregar filas", e);
        toast.error("Não foi possível carregar as filas.");
      });

    api
      .get("/Employee/employees")
      .then((r) => {
        const list = (Array.isArray(r.data) ? r.data : []).filter((e: any) => e.flActive);
        setEmployees(list.map((e: any) => ({ id: e.id, name: e.nmEmployee ?? e.email, email: e.email })));
      })
      .catch((e) => console.error("Erro ao carregar colaboradores", e));

    api
      .get("/Client/Clients")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setClients(list.map((c: any) => ({ id: c.id, name: c.tradeName || c.legalName || `Cliente #${c.id}` })));
      })
      .catch((e) => console.error("Erro ao carregar clientes", e));
  }, [carregarCategorias]);

  // Um filtro só, montado uma vez, para as duas consultas: lista e indicador contam os mesmos chamados.
  const filtro = useMemo(
    () => ({
      month,
      clientId,
      queueId,
      assigneeEmployeeId: assignee && assignee !== SEM_RESPONSAVEL ? Number(assignee) : null,
      unassigned: assignee === SEM_RESPONSAVEL,
    }),
    [month, clientId, queueId, assignee]
  );

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    getTickets(filtro)
      .then((data) => !cancelado && setTickets(data))
      .catch((e: any) => {
        if (!cancelado) toast.error(e?.response?.data?.message ?? "Não foi possível carregar os chamados.");
      })
      .finally(() => !cancelado && setLoading(false));
    getTicketSlaCompliance(filtro)
      .then((d) => !cancelado && setSla(d))
      .catch((e: any) => {
        if (!cancelado) toast.error(e?.response?.data?.message ?? "Não foi possível carregar o indicador de SLA.");
      });
    return () => {
      cancelado = true;
    };
  }, [filtro, reload]);

  const recarregar = useCallback(() => setReload((k) => k + 1), []);

  const contagem = useMemo(
    () => Object.fromEntries(ABAS.map((a) => [a.key, tickets.filter(a.filtro).length])) as Record<Aba, number>,
    [tickets]
  );
  const visiveis = useMemo(() => tickets.filter(ABAS.find((a) => a.key === aba)!.filtro), [tickets, aba]);
  const semResponsavelAbertos = useMemo(
    () => tickets.filter((t) => emAberto(t) && t.assigneeEmployeeId == null).length,
    [tickets]
  );

  const alternarDetalhes = () =>
    setDetalhes((v) => {
      try {
        localStorage.setItem(DETALHES_KEY, v ? "0" : "1");
      } catch {
        /* preferência só deste navegador; sem storage, segue recolhido */
      }
      return !v;
    });

  const kpis: KpiItem[] = [
    {
      key: "resolucao",
      label: "Resolvidos no prazo",
      value: fmtPercent(sla?.resolution.percent),
      valueClassName: TONE_TEXT[slaTone(sla?.resolution.percent)],
      hint: sla ? `${sla.resolution.onTime} de ${sla.resolution.onTime + sla.resolution.late}` : "…",
    },
    {
      key: "resposta",
      label: "1ª resposta no prazo",
      value: fmtPercent(sla?.response.percent),
      valueClassName: TONE_TEXT[slaTone(sla?.response.percent)],
      hint: sla ? `${sla.response.onTime} de ${sla.response.onTime + sla.response.late}` : "…",
    },
    {
      key: "abertos",
      label: "Em aberto",
      value: contagem.abertos,
      onClick: () => setAba("abertos"),
      active: aba === "abertos",
    },
    {
      key: "atrasados",
      label: "Atrasados em aberto",
      value: contagem.atrasados,
      valueClassName: contagem.atrasados > 0 ? "text-red-600 dark:text-red-400" : undefined,
      onClick: () => setAba("atrasados"),
      active: aba === "atrasados",
    },
    {
      key: "sem-responsavel",
      label: "Sem responsável",
      value: semResponsavelAbertos,
      valueClassName: semResponsavelAbertos > 0 ? "text-amber-600 dark:text-amber-400" : undefined,
      hint: "em aberto",
      onClick: () => setAssignee((v) => (v === SEM_RESPONSAVEL ? null : SEM_RESPONSAVEL)),
      active: assignee === SEM_RESPONSAVEL,
    },
  ];

  const opcoesColaborador = [
    ...(myEmployeeId != null ? [{ label: "Meus chamados", value: String(myEmployeeId) }] : []),
    { label: "Sem responsável", value: SEM_RESPONSAVEL },
    ...employees.filter((e) => e.id !== myEmployeeId).map((e) => ({ label: e.name, value: String(e.id) })),
  ];

  return (
    <div className="mt-5 flex flex-col gap-4 animate-fade-up">
      <PageHeader
        icon={<MdConfirmationNumber className="h-5 w-5" />}
        title="Chamados"
        actions={
          <>
            {canAdmin && (
              <button
                type="button"
                onClick={() => setCategoriesOpen(true)}
                className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
              >
                <MdCategory className="h-4 w-4" aria-hidden="true" />
                Categorias
              </button>
            )}
            {canManage && (
              <button
                type="button"
                onClick={() => setNewOpen(true)}
                className="flex items-center justify-center gap-1.5 rounded-xl bg-ink-900 px-4 py-2 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
              >
                <MdAdd className="h-4 w-4" aria-hidden="true" />
                Abrir chamado
              </button>
            )}
          </>
        }
        filters={
          <>
            <FilterField label="Mês de abertura" htmlFor="tickets-month">
              <select
                id="tickets-month"
                value={month ?? ""}
                onChange={(e) => setMonth(e.target.value === "" ? null : e.target.value)}
                className={selectCls}
              >
                <option value="">Todos os meses</option>
                {ultimosMeses().map((m) => (
                  <option key={m} value={m}>
                    {labelMes(m)}
                  </option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Cliente">
              <SearchableSelect
                placeholder="Todos os clientes"
                value={clientId}
                onChange={(v) => setClientId(v == null ? null : Number(v))}
                options={clients.map((c) => ({ label: c.name, value: c.id }))}
              />
            </FilterField>
            <FilterField label="Fila">
              <SearchableSelect
                placeholder="Todas as filas"
                value={queueId}
                onChange={(v) => setQueueId(v == null ? null : Number(v))}
                options={queues.map((q) => ({ label: q.name, value: q.id }))}
              />
            </FilterField>
            <FilterField label="Colaborador">
              <SearchableSelect
                placeholder="Todos os colaboradores"
                value={assignee}
                onChange={(v) => setAssignee(v == null ? null : String(v))}
                options={opcoesColaborador}
              />
            </FilterField>
          </>
        }
        kpis={<KpiStrip items={kpis} />}
      >
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={alternarDetalhes}
            aria-expanded={detalhes}
            aria-controls="tickets-sla-detalhes"
            className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-dv-blue-600 hover:bg-dv-blue-500/10 dark:text-dv-cyan-300"
          >
            {detalhes ? <MdExpandLess className="h-4 w-4" aria-hidden="true" /> : <MdExpandMore className="h-4 w-4" aria-hidden="true" />}
            {detalhes ? "Ocultar detalhes do SLA" : "Detalhes do SLA"}
          </button>
        </div>
        {detalhes && (
          <div id="tickets-sla-detalhes" className="mt-2 border-t border-gray-100 pt-4 dark:border-white/10">
            {sla ? (
              <SlaDetails
                data={sla}
                selectedAssigneeId={filtro.assigneeEmployeeId}
                selectedUnassigned={filtro.unassigned}
                onSelectAssignee={(id) => setAssignee(id == null ? SEM_RESPONSAVEL : String(id))}
              />
            ) : (
              <p className="py-4 text-center text-sm text-gray-700 dark:text-gray-300">Carregando…</p>
            )}
          </div>
        )}
      </PageHeader>

      <Card extra="p-4 sm:p-5">
        <div role="tablist" aria-label="Estado dos chamados" className="mb-3 flex gap-1 overflow-x-auto">
          {ABAS.map((a) => (
            <button
              key={a.key}
              type="button"
              role="tab"
              aria-selected={aba === a.key}
              onClick={() => setAba(a.key)}
              className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
                aba === a.key
                  ? "bg-navy-700 text-white dark:bg-white dark:text-navy-900"
                  : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
              }`}
            >
              {a.label}
              <span className="tabular-nums text-xs opacity-75">{contagem[a.key]}</span>
            </button>
          ))}
        </div>
        {loading ? (
          <SkeletonTable active={loading} />
        ) : (
          <TicketTable tickets={visiveis} loading={loading} onOpen={(t) => setSelectedTicketId(t.id)} />
        )}
      </Card>

      {selectedTicketId != null && (
        <TicketDetailModal
          ticketId={selectedTicketId}
          employees={employees}
          queues={queues}
          categories={categories}
          myEmployeeId={myEmployeeId}
          canManage={canManage}
          canAdmin={canAdmin}
          onClose={() => setSelectedTicketId(null)}
          onChanged={recarregar}
        />
      )}

      <NewTicketModal
        open={newOpen}
        clients={clients}
        queues={queues}
        categories={categories}
        onClose={() => setNewOpen(false)}
        onCreated={recarregar}
      />

      <CategoriesModal
        open={categoriesOpen}
        queues={queues}
        onClose={() => setCategoriesOpen(false)}
        // Recarrega o catálogo dos formulários E a lista: mudar o roteamento de uma categoria não
        // mexe em chamado já aberto, mas renomear uma muda o que a coluna "Categoria" mostra.
        onChanged={() => {
          carregarCategorias();
          recarregar();
        }}
      />
    </div>
  );
};

export default TicketsPage;
